import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { useConsoleStore } from '../store';

type StoreRow = {
  id: string;
  externalDomain: string;
  platform: string;
  isActive: boolean;
  planTier: string;
  subscriptionStatus: string;
  billingProvider: string;
  createdAt: string;
};

const PLAN_ORDER = ['free', 'growth', 'agent', 'rep', 'vea'] as const;

function formatDate(v?: string): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function platformBadge(platform?: string) {
  const p = (platform || '').toLowerCase();
  if (p === 'shopify') {
    return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Shopify</span>;
  }
  if (p === 'wordpress') {
    return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">WordPress</span>;
  }
  return <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-700">{platform || 'Unknown'}</span>;
}

function planBadge(plan?: string) {
  const p = (plan || '').toLowerCase();
  const map: Record<string, string> = {
    free: 'bg-zinc-100 text-zinc-700',
    growth: 'bg-cyan-100 text-cyan-700',
    agent: 'bg-violet-100 text-violet-700',
    rep: 'bg-amber-100 text-amber-700',
    vea: 'bg-rose-100 text-rose-700',
  };
  const cls = map[p] || 'bg-zinc-100 text-zinc-700';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{plan || 'unknown'}</span>;
}

function statusBadge(status?: string) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'active'
      ? 'bg-emerald-100 text-emerald-700'
      : s === 'trialing'
        ? 'bg-sky-100 text-sky-700'
        : s === 'past_due'
          ? 'bg-amber-100 text-amber-700'
          : s === 'cancelled'
            ? 'bg-zinc-200 text-zinc-700'
            : 'bg-zinc-100 text-zinc-700';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{status || 'unknown'}</span>;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-zinc-900">{value}</p>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useConsoleStore();
  const role = (user?.role || '').toLowerCase();
  const canStaff = role === 'staff' || role === 'superadmin';

  const storesQuery = useQuery({
    queryKey: ['internal-stores'],
    queryFn: async () => {
      const data = await apiFetch<any>('/api/internal/stores');
      if (Array.isArray(data)) return data as StoreRow[];
      if (Array.isArray(data?.stores)) return data.stores as StoreRow[];
      return [] as StoreRow[];
    },
  });

  const stores = storesQuery.data || [];

  const stats = useMemo(() => {
    const total = stores.length;
    const active = stores.filter((s) => s.isActive).length;
    const paid = stores.filter((s) => (s.planTier || '').toLowerCase() !== 'free').length;
    const shopify = stores.filter((s) => (s.platform || '').toLowerCase() === 'shopify').length;
    const wordpress = stores.filter((s) => (s.platform || '').toLowerCase() === 'wordpress').length;
    const standard = stores.filter((s) => !['shopify', 'wordpress'].includes((s.platform || '').toLowerCase())).length;
    const grace = stores.filter((s) => (s.subscriptionStatus || '').toLowerCase() === 'past_due').length;
    return { total, active, paid, shopify, wordpress, standard, grace };
  }, [stores]);

  const planDist = useMemo(() => {
    const total = stores.length || 1;
    return PLAN_ORDER.map((tier) => {
      const count = stores.filter((s) => (s.planTier || '').toLowerCase() === tier).length;
      const pct = Math.round((count / total) * 100);
      return { tier, count, pct };
    });
  }, [stores]);

  const recentStores = useMemo(() => {
    return [...stores]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 10);
  }, [stores]);

  return (
    <div className="space-y-6 p-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Total Stores" value={stats.total} />
        <StatCard label="Active Stores" value={stats.active} />
        <StatCard label="Paid Stores" value={stats.paid} />
        <StatCard label="Shopify" value={stats.shopify} />
        <StatCard label="WordPress" value={stats.wordpress} />
        <StatCard label="Standard Sites" value={stats.standard} />
        <StatCard label="Trials Expiring (7d)" value="—" />
        <StatCard label="In Grace Period" value={stats.grace} />
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-zinc-900">Plan Distribution</h2>
        <div className="space-y-3">
          {planDist.map((row) => (
            <div key={row.tier}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="capitalize text-zinc-700">{row.tier}</span>
                <span className="text-zinc-500">{row.count} ({row.pct}%)</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                <div className="h-full rounded-full bg-zinc-800" style={{ width: `${row.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-zinc-900">Recent Stores</h2>
        {storesQuery.isLoading && <p className="text-sm text-zinc-500">Loading stores...</p>}
        {!storesQuery.isLoading && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-zinc-500">
                <tr className="border-b border-zinc-200">
                  <th className="px-3 py-2 font-medium">Domain</th>
                  <th className="px-3 py-2 font-medium">Platform</th>
                  <th className="px-3 py-2 font-medium">Plan</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {recentStores.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-100">
                    <td className="px-3 py-2">
                      <button
                        onClick={() => navigate(`/admin?domain=${encodeURIComponent(s.externalDomain)}`)}
                        className="font-medium text-zinc-900 underline"
                      >
                        {s.externalDomain}
                      </button>
                    </td>
                    <td className="px-3 py-2">{platformBadge(s.platform)}</td>
                    <td className="px-3 py-2">{planBadge(s.planTier)}</td>
                    <td className="px-3 py-2">{statusBadge(s.subscriptionStatus)}</td>
                    <td className="px-3 py-2">{formatDate(s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canStaff && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-6">
          <h2 className="mb-4 text-lg font-semibold text-zinc-900">Quick Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="rounded-xl bg-zinc-900 px-4 py-2 text-sm font-medium text-white"
            >
              Add Store
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="rounded-xl border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            >
              View All Stores
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
